import {Label, Segment} from 'platform/components';
import {HStack, Text, VStack} from 'platform/foundation';

import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {getBodyStyleCategory} from '../../../../../../utils/getBodyStyleCategory';
import {VehicleCategoryFilter} from '../../../../types/VehicleCategoryFilter';
import {useFilters} from '../../../FiltersContext/FiltersContext';

/** Segment value for "all categories" – stored as `null` in the filters */
const ALL_CATEGORIES = 'ALL';

type VehicleCategorySegmentValue = VehicleCategoryFilter | typeof ALL_CATEGORIES;

/**
 * Top-level "Kategorie vozu" switch (passenger cars / vans / all).
 * Changing the category drops body styles that no longer belong to it, so a saved
 * `CARSTYLE_*` selection never silently limits a vans-only search. Switching to passenger cars
 * also drops the van body length / height classes, which are hidden there.
 */
export function FilterVehicleCategory(props: TestIdProps) {
  const {filters, onUpdateFilters} = useFilters();

  const value: VehicleCategorySegmentValue = filters.vehicleCategory ?? ALL_CATEGORIES;

  const handleChange = (nextValue: VehicleCategorySegmentValue) => {
    const nextCategory = nextValue === ALL_CATEGORIES ? null : nextValue;
    onUpdateFilters('vehicleCategory', nextCategory);

    if (isNil(nextCategory)) {
      return;
    }

    // Only clear what is set: every `onUpdateFilters` call also pushes a filter_changed GTM event.
    if (nextCategory === VehicleCategoryFilter.CAR) {
      if (isNotNilOrEmpty(filters.vehicleLengthCategory)) {
        onUpdateFilters('vehicleLengthCategory', undefined);
      }
      if (isNotNilOrEmpty(filters.vehicleHeightCategory)) {
        onUpdateFilters('vehicleHeightCategory', undefined);
      }
    }

    const bodyTypeInCategory = filters.bodyType?.filter(
      (key) => getBodyStyleCategory(key) === nextCategory
    );
    if (bodyTypeInCategory?.length !== filters.bodyType?.length) {
      onUpdateFilters('bodyType', bodyTypeInCategory?.length ? bodyTypeInCategory : undefined);
    }
  };

  return (
    <VStack spacing={1} data-testid={suffixTestId('filterVehicleCategory', props)}>
      <Label>{i18n.t('entity.sourcing.filters.labels.vehicleCategory')}</Label>
      <HStack spacing={4} align="center">
        <Segment<VehicleCategorySegmentValue>
          value={value}
          onChange={handleChange}
          options={[
            {
              value: VehicleCategoryFilter.CAR,
              label: i18n.t('entity.sourcing.filters.labels.vehicleCategoryCar'),
            },
            {
              value: VehicleCategoryFilter.VAN,
              label: i18n.t('entity.sourcing.filters.labels.vehicleCategoryVan'),
            },
            {
              value: ALL_CATEGORIES,
              label: i18n.t('entity.sourcing.filters.labels.vehicleCategoryAll'),
            },
          ]}
          data-testid={suffixTestId('vehicleCategory', props)}
        />
        <Text
          size="xSmall"
          color="tertiary"
          data-testid={suffixTestId('vehicleCategoryDescription', props)}
        >
          {i18n.t('entity.sourcing.filters.labels.vehicleCategoryDescription')}
        </Text>
      </HStack>
    </VStack>
  );
}
